// src/components/common/StatusBadge.jsx
// -----------------------------------------------------------------------------
// Colored chip used to display ticket Status / Severity / Role / user Status
// values consistently across the entire project.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import '../../styles/theme.css';
import './common.css';

// Normalize status string for resilient key lookup
function normalizeStatusKey(val) {
  if (!val && val !== 0) return '';
  return String(val)
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]+/g, '');
}

const STATUS_STYLE_MAP = {
  // OPEN / NEW (Sunset Orange)
  open: { className: 'badge-status-open', label: 'OPEN' },
  new: { className: 'badge-status-open', label: 'NEW' },

  // IN PROGRESS (Vibrant Blue)
  inprogress: { className: 'badge-status-inprogress', label: 'IN PROGRESS' },
  in_progress: { className: 'badge-status-inprogress', label: 'IN PROGRESS' },
  dev: { className: 'badge-status-inprogress', label: 'DEVELOPMENT' },
  development: { className: 'badge-status-inprogress', label: 'DEVELOPMENT' },

  // QA-TESTING / TESTING (Royal Purple)
  qatesting: { className: 'badge-status-qa', label: 'QA TESTING' },
  qa_testing: { className: 'badge-status-qa', label: 'QA TESTING' },
  qa: { className: 'badge-status-qa', label: 'QA' },
  testing: { className: 'badge-status-qa', label: 'TESTING' },
  test: { className: 'badge-status-qa', label: 'TEST' },
  review: { className: 'badge-status-qa', label: 'REVIEW' },
  codereview: { className: 'badge-status-qa', label: 'CODE REVIEW' },

  // RESOLVED / FIXED (Emerald Green)
  resolved: { className: 'badge-status-resolved', label: 'RESOLVED' },
  resolve: { className: 'badge-status-resolved', label: 'RESOLVED' },
  fixed: { className: 'badge-status-resolved', label: 'FIXED' },

  // CLOSED / COMPLETED (Deep Teal Slate)
  closed: { className: 'badge-status-closed', label: 'CLOSED' },
  close: { className: 'badge-status-closed', label: 'CLOSED' },
  completed: { className: 'badge-status-closed', label: 'COMPLETED' },
  done: { className: 'badge-status-closed', label: 'DONE' },

  // REOPENED (Amber Orange)
  reopened: { className: 'badge-status-reopened', label: 'REOPENED' },
  reopen: { className: 'badge-status-reopened', label: 'REOPENED' },

  // OVERDUE / CRITICAL / BLOCKED (Red)
  overdue: { className: 'badge-status-danger', label: 'OVERDUE' },
  blocked: { className: 'badge-status-danger', label: 'BLOCKED' },
  critical: { className: 'badge-status-danger', label: 'CRITICAL' },
  // ACTIVE (Emerald Green with Glow)
  active: { className: 'badge-status-active', label: 'ACTIVE' },
  enabled: { className: 'badge-status-active', label: 'ENABLED' },
  true: { className: 'badge-status-active', label: 'ACTIVE' },
  yes: { className: 'badge-status-active', label: 'YES' },

  // INACTIVE (Soft Crimson Rose Red with Glow)
  inactive: { className: 'badge-status-inactive', label: 'INACTIVE' },
  deactivated: { className: 'badge-status-inactive', label: 'DEACTIVATED' },
  disabled: { className: 'badge-status-inactive', label: 'DISABLED' },
  false: { className: 'badge-status-inactive', label: 'INACTIVE' },
  no: { className: 'badge-status-inactive', label: 'NO' },
  locked: { className: 'badge-status-inactive', label: 'LOCKED' },
};

export default function StatusBadge({ value }) {
  if (!value && value !== 0) return null;
  const rawStr = String(value).trim();
  const normalizedKey = normalizeStatusKey(rawStr);

  const matched = STATUS_STYLE_MAP[normalizedKey];
  const className = matched ? matched.className : 'badge-status-default';
  const displayLabel = rawStr.toUpperCase().replace(/_/g, ' ');

  return (
    <span className={`badge ${className}`}>
      {displayLabel}
    </span>
  );
}

StatusBadge.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};